import {useEffect, useMemo, useRef, useState} from 'react';

import PropTypes from 'prop-types';

import {Box, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Video as BaseVideo} from '@amblique/base/app/components/shared/video';
import {ContentfulVideoPlayOverlay} from '@amblique/king-living/app/contentful/models/video-play-overlay';
import {useBlogArticleVideoChrome} from '@amblique/king-living/app/hooks/use-blog-article-video-chrome';
import {inflector} from '@amblique/king-living/shared/helpers/inflector';
import {normalizeVideoId} from '@amblique/king-living/app/utils/video-utils';

const FILL_STICKY_BAND_ROOT_SX = {
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  minHeight: 0,
  minWidth: 0,
  position: 'relative',
  width: '100%',
};

const FILL_STICKY_BAND_BASE_VIDEO_PROPS = {
  aspectRatio: 'unset',
  flex: 1,
  minHeight: 0,
  width: '100%',
};

/**
 * Contentful-backed video; forwards to BaseVideo with optional theme presets.
 */
export const Video = ({
  component,
  isComplete = true,
  isFetching = false,
  fillStickyBand = false,
  ...props
}) => {
  const {videoIdOrUrl, videoAsset, presets, options} = component;

  const blogChrome = useBlogArticleVideoChrome();
  const [chromeHovered, setChromeHovered] = useState(false);

  const styles = useMultiStyleConfig('contentful/video', {
    component,
    isComplete,
    isFetching,
    variant: blogChrome ? 'blogArticle' : 'default',
  });

  useEffect(() => {
    if (!blogChrome) setChromeHovered(false);
  }, [blogChrome]);

  const normalizedPresets = useMemo(() => {
    return typeof presets === 'string' ? inflector.camelize(presets) : null;
  }, [presets]);

  const {id} = useMemo(() => {
    if (!isComplete && isFetching) {
      return {id: null};
    }

    if (videoIdOrUrl) {
      return {id: normalizeVideoId(videoIdOrUrl)};
    }

    if (videoAsset?.url) {
      return {id: `mobile: ${videoAsset.url}`};
    }

    return {id: null};
  }, [videoIdOrUrl, videoAsset, isComplete, isFetching]);

  const [abstractVideo, setAbstractVideo] = useState(null);
  /** Base Video exposes imperative API via ref; callback refs may not re-fire when lazy-init completes. */
  const videoRef = useRef(null);

  useEffect(() => {
    setAbstractVideo(null);
  }, [id]);

  useEffect(() => {
    if (!id) return undefined;

    let cancelled = false;

    const syncRef = () => {
      const instance = videoRef.current;
      if (cancelled || instance == null) return;

      setAbstractVideo((prev) => (prev === instance ? prev : instance));
    };

    syncRef();
    const intervalId = window.setInterval(syncRef, 120);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [id]);

  if (!id) return null;
  if (!isComplete && isFetching) return null;

  return (
    <Box {...styles.root} {...(fillStickyBand ? {sx: FILL_STICKY_BAND_ROOT_SX} : {})} {...props}>
      <BaseVideo
        ref={videoRef}
        id={id}
        options={options}
        presets={normalizedPresets}
        {...styles.video}
        {...(fillStickyBand ? FILL_STICKY_BAND_BASE_VIDEO_PROPS : {})}
      />
      <ContentfulVideoPlayOverlay
        abstractVideo={abstractVideo}
        blogChrome={blogChrome}
        chromeHovered={chromeHovered}
        overlayStyles={{overlay: styles.overlay, overlayButton: styles.overlayButton}}
        onChromeHoveredChange={setChromeHovered}
      />
    </Box>
  );
};

Video.displayName = 'Video';
Video.propTypes = {
  component: PropTypes.object.isRequired,
  fillStickyBand: PropTypes.bool,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
